import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Stack,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { describeTokenActionError, isTokenGone } from "./tokenErrors";
import {
  MAX_ROTATION_INTERVAL_DAYS,
  MIN_ROTATION_INTERVAL_DAYS,
  setRepoHostTokenRotationInterval,
  type RepoHostTokenSummary,
} from "./tokensApi";

export interface RotationIntervalModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly role: string;
  readonly current: number | null;
  readonly onChanged: () => void;
}

type BodyProps = Omit<RotationIntervalModalProps, "opened">;

// Тело формы — монтируется только когда модалка открыта (сброс state).
function RotationIntervalBody(props: BodyProps): ReactElement {
  const { t } = useTranslation();
  const { onClose, role, current, onChanged } = props;
  const queryClient = useQueryClient();

  const [never, setNever] = useState(current === null);
  const [days, setDays] = useState<number | string>(current ?? "");
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation<RepoHostTokenSummary, Error, number | null>({
    mutationFn: (value) => setRepoHostTokenRotationInterval(role, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "tokens"],
      });
      onChanged();
      onClose();
    },
    onError: (err) => {
      setFormError(describeTokenActionError("rotationInterval", err, t));
      if (isTokenGone(err)) {
        onChanged();
      }
    },
  });

  const handleSave = (): void => {
    setFormError(null);
    if (never) {
      mutation.mutate(null);
      return;
    }
    if (
      typeof days !== "number" ||
      days < MIN_ROTATION_INTERVAL_DAYS ||
      days > MAX_ROTATION_INTERVAL_DAYS
    ) {
      setFormError(t("connections.tokens.rotationModal.invalidDays"));
      return;
    }
    mutation.mutate(days);
  };

  return (
    <Stack gap="sm">
      {formError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {formError}
        </Alert>
      ) : null}

      <Checkbox
        label={t("connections.tokens.rotationModal.neverLabel")}
        description={t("connections.tokens.rotationModal.neverHint")}
        checked={never}
        onChange={(e) => {
          setNever(e.currentTarget.checked);
        }}
      />

      <NumberInput
        label={t("connections.tokens.rotationModal.daysLabel")}
        description={t("connections.tokens.rotationModal.daysHint")}
        value={days}
        onChange={setDays}
        min={MIN_ROTATION_INTERVAL_DAYS}
        max={MAX_ROTATION_INTERVAL_DAYS}
        allowDecimal={false}
        allowNegative={false}
        clampBehavior="none"
        disabled={never}
      />

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("connections.modalCancel")}
        </Button>
        <Button onClick={handleSave} loading={mutation.isPending}>
          {t("connections.tokens.rotationModal.saveButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Модалка PUT /tokens/{role}/rotation-interval — число дней или «никогда».
export function RotationIntervalModal(
  props: RotationIntervalModalProps,
): ReactElement {
  const { t } = useTranslation();
  const { opened, onClose, role, current, onChanged } = props;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("connections.tokens.rotationModal.title", { role })}
      size="sm"
    >
      {opened ? (
        <RotationIntervalBody
          onClose={onClose}
          role={role}
          current={current}
          onChanged={onChanged}
        />
      ) : null}
    </Modal>
  );
}
